"use client";

import {
  FormEvent,
  type ReactNode,
  useActionState,
  useEffect,
  useRef,
  useState,
} from "react";

import cn from "classnames";
import { brown } from "@/app/font";

import {
  type EmailCaptureState,
  subscribeEmail,
} from "@/app/actions/subscribeEmail";
import { EVENTS } from "@/constants/analytics.constants";
import { FORM_EVENTS } from "@/constants/formAnalytics.constants";
import { useFormSubmitAttemptTag } from "@/hooks/useFormSubmitAttemptTag";
import useTagging from "@/hooks/useTagging";

export const AdtEmailSignupForm = ({
  inputId,
  headingId,
  title,
  successHeading = "Thank you for signing up.",
  requiredFieldsLabel,
  emailPlaceholder,
  submitLabel,
  contentClassName,
  formLocation = "footer",
  children,
}: AdtEmailSignupFormProps) => {
  const [state, formAction, isPending] = useActionState(
    subscribeEmail,
    INITIAL_STATE,
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>();
  const errorId = `${inputId}-error`;
  const successId = `${inputId}-success`;
  const [, setTag] = useTagging();
  const prevStateRef = useRef(state);
  const formEvent =
    formLocation === "header"
      ? FORM_EVENTS.HEADER_EMAIL.formEvent
      : FORM_EVENTS.FOOTER_EMAIL.formEvent;

  useFormSubmitAttemptTag(isPending, formEvent);

  useEffect(() => {
    if (prevStateRef.current === state) return;
    prevStateRef.current = state;

    if (state.status === "error") {
      setError(state.message);
      inputRef.current?.focus();
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...formEvent,
          fieldName: "E_Mail_Address",
          errorCode: state.message,
        },
      });
    }

    if (state.status === "success") {
      setError(undefined);
      setTag({
        event: EVENTS.FORM_SUBMIT_COMPLETE,
        outcome: "success",
        formEvent,
      });
    }
  }, [state, setTag, formEvent]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const nextError = validateEmail(email);
    if (nextError) {
      event.preventDefault();
      setError(nextError);
      inputRef.current?.focus();
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...formEvent,
          fieldName: "E_Mail_Address",
          errorCode: nextError,
        },
      });
      return;
    }

    setError(undefined);
  };

  const describedBy = error
    ? errorId
    : state.status === "success"
      ? successId
      : undefined;

  if (formLocation === "header" && state.status === "success") {
    return (
      <h2
        id={headingId}
        className={cn(
          brown.className,
          "headline-5 text-adt-blue max-w-none px-6 py-8 lg:px-16 lg:py-6",
        )}>
        {successHeading}
      </h2>
    );
  }

  const form = (
    <form
      action={formAction}
      onSubmit={handleSubmit}
      noValidate
      data-testid={`${inputId}-form`}
      className="flex flex-col gap-3">
      <input type="hidden" name="source" value="Email signup" />
      <label htmlFor={inputId} className="body-6">
        {requiredFieldsLabel}
      </label>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <input
            ref={inputRef}
            id={inputId}
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder={emailPlaceholder}
            value={email}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(event) => setEmail(event.currentTarget.value)}
            className={cn(
              "body-1 border-greyscale-strength text-greyscale-headline placeholder:text-greyscale-body focus-visible:outline-secondary min-h-12 flex-1 rounded-full border bg-white px-5 focus-visible:outline-2 focus-visible:outline-offset-2",
              error && "border-error focus-visible:outline-error",
            )}
          />
          {error ? (
            <p id={errorId} role="alert" className="text-error px-3 text-sm">
              {error}
            </p>
          ) : null}
          {state.status === "success" && !error ? (
            <p
              id={successId}
              role="status"
              aria-live="polite"
              className="body-6 text-adt-blue px-3 font-bold">
              {state.message}
            </p>
          ) : null}
        </div>
        <button
          type="submit"
          disabled={isPending}
          aria-busy={isPending}
          className={cn(
            brown.className,
            "button bg-adt-blue hover:bg-secondary-100 focus-visible:outline-adt-blue min-h-12 rounded-full px-10 text-white focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70",
          )}>
          {submitLabel}
        </button>
      </div>
      <div className="body-6 max-w-2xl">{children}</div>
    </form>
  );

  if (!title) return form;

  return (
    <div className={contentClassName}>
      <h2
        id={headingId}
        className={cn(brown.className, "headline-5 text-adt-blue max-w-none")}>
        {title}
      </h2>
      {form}
    </div>
  );
};

const validateEmail = (value: string) => {
  const email = value.trim();
  if (!email) return "Email address is required";
  if (!EMAIL_PATTERN.test(email)) return "Please enter a valid email address";
};

export type AdtEmailSignupFormProps = {
  inputId: string;
  headingId?: string;
  title?: string;
  successHeading?: string;
  requiredFieldsLabel: string;
  emailPlaceholder: string;
  submitLabel: string;
  contentClassName?: string;
  formLocation?: "header" | "footer";
  children?: ReactNode;
};

const INITIAL_STATE: EmailCaptureState = { status: "idle" };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
